import React, { useState, useRef, useEffect } from 'react';
import { Upload, File, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

/**
 * DriversLicenseUploadStep Component
 * 
 * Step 2 of Physician ID Proofing: Drivers License Upload
 * 
 * Features:
 * - Drag and drop file upload
 * - File picker with validation
 * - Image preview functionality
 * - File management (remove/replace)
 * - Loading states and accessibility
 * - Basic validation (type, size) - humans will verify content
 */

interface DriversLicenseUploadStepProps {
  onNext: () => void;
  onBack: () => void;
  onFileUpload: (file: File | null) => void;
  uploadedFile?: File | null;
  isLoading: boolean;
}

// Allowed file types and size limits
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// File validation function
function validateFile(file: File): { isValid: boolean; error?: string } {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return { isValid: false, error: 'Please select a PDF, JPG, or PNG file' };
  }
  
  if (file.size > MAX_FILE_SIZE) {
    return { isValid: false, error: 'File size must be less than 5MB' };
  }
  
  return { isValid: true };
}

// Format file size for display
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0.0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(1) + ' ' + sizes[i];
}

export const DriversLicenseUploadStep: React.FC<DriversLicenseUploadStepProps> = ({
  onNext,
  onBack,
  onFileUpload,
  uploadedFile,
  isLoading
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate preview URL for image files
  useEffect(() => {
    if (uploadedFile && uploadedFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(uploadedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl('');
    }
  }, [uploadedFile]);

  // Handle file selection
  const handleFileSelect = (file: File) => {
    setError('');
    const validation = validateFile(file);
    
    if (!validation.isValid) {
      setError(validation.error || 'Invalid file');
      return;
    }
    
    onFileUpload(file);
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle drag events
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle click on drop zone
  const handleDropZoneClick = () => {
    if (!isLoading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle keyboard activation of drop zone
  const handleDropZoneKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !isLoading) {
      e.preventDefault();
      handleDropZoneClick();
    }
  };

  // Handle file removal
  const handleRemoveFile = () => {
    setError('');
    onFileUpload(null);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (uploadedFile && !isLoading) {
      onNext();
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <form role="form" onSubmit={handleSubmit} className="space-y-6">
        {/* Step Header */}
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Drivers License Upload
          </h2>
          <p className="text-gray-600">
            Upload a clear photo or scan of your drivers license for verification
          </p>
        </div>

        {/* File Requirements */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">File Requirements</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Accepted formats: PDF, JPG, PNG</li>
            <li>• Maximum file size: 5MB</li>
            <li>• Ensure all text is clearly visible and readable</li>
            <li>• Make sure the license is not expired</li>
          </ul>
        </div>

        {/* Upload Area */}
        {!uploadedFile ? (
          <div className="space-y-4">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileInputChange}
              disabled={isLoading}
              className="hidden"
              tabIndex={-1}
              aria-label="Upload drivers license file input"
              aria-describedby="upload-help upload-error"
            />

            {/* Drop Zone */}
            <div
              data-testid="file-upload-area"
              role="button"
              tabIndex={0}
              aria-label="Drag and drop area for drivers license upload"
              className={`
                relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                ${dragOver ? 'border-blue-500 bg-blue-50 drag-over' : 'border-gray-300 hover:border-gray-400'}
                ${isLoading ? 'cursor-not-allowed opacity-50' : ''}
              `}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleDropZoneClick}
              onKeyDown={handleDropZoneKeyDown}
            >
              <div className="flex flex-col items-center">
                {isLoading ? (
                  <Loader2 data-testid="upload-loading-spinner" className="h-12 w-12 text-blue-500 animate-spin mb-4" />
                ) : (
                  <Upload className="h-12 w-12 text-gray-400 mb-4" />
                )}
                <p className="text-lg font-medium text-gray-700 mb-2">
                  {isLoading ? 'Uploading...' : 'Drop your drivers license here'}
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  {isLoading ? 'Please wait while we process your file' : 'or click to browse files'}
                </p>
                <button
                  type="button"
                  disabled={isLoading}
                  className={`
                    px-4 py-2 bg-blue-600 text-white rounded-lg font-medium
                    ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'}
                  `}
                >
                  Choose File
                </button>
              </div>
            </div>

            {/* Help Text */}
            <p id="upload-help" className="text-sm text-gray-500 text-center">
              Supported formats: PDF, JPG, PNG • Maximum size: 5MB
            </p>

            {/* Error Message */}
            {error && (
              <div id="upload-error" className="flex items-center text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>
        ) : (
          /* Uploaded File Display */
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-start space-x-4">
              {/* File Preview or Icon */}
              <div className="flex-shrink-0">
                {previewUrl ? (
                  <img
                    data-testid="file-preview-image"
                    src={previewUrl}
                    alt="Drivers license preview"
                    className="w-16 h-16 object-cover rounded border"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded border flex items-center justify-center">
                    <File className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <CheckCircle data-testid="file-success-icon" className="h-5 w-5 text-green-500" />
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {uploadedFile.name}
                  </p>
                </div>
                <p className="text-sm text-gray-500">
                  {formatFileSize(uploadedFile.size)} • {uploadedFile.type}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  File uploaded successfully
                </p>
              </div>

              {/* Remove Button */}
              <button
                type="button"
                onClick={handleRemoveFile}
                disabled={isLoading}
                className={`
                  p-2 text-gray-400 hover:text-red-500 rounded-lg
                  ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-50'}
                `}
                aria-label="Remove file"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className={`
              px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500
              ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            Back
          </button>

          <button
            type="submit"
            disabled={!uploadedFile || isLoading}
            className={`
              px-6 py-2 rounded-lg text-white font-medium focus:ring-2 focus:ring-blue-500
              ${uploadedFile && !isLoading
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-400 cursor-not-allowed'
              }
            `}
          >
            {isLoading ? (
              <span className="flex items-center">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Uploading...
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}; 